import { useEffect, useState } from 'react';
import { Button, Modal, Spin, Tag, Tabs, message } from 'antd';
import { CloudServerOutlined, DatabaseOutlined, DownOutlined, KeyOutlined, LockOutlined, MailOutlined, RightOutlined } from '@ant-design/icons';
import {
  treasureGetDatabase,
  treasureGetServer,
  treasureSendDatabaseCredentials,
  treasureSendServerCredentials,
  type DatabaseGrant,
  type ServerGrant,
} from '@/services/treasure';
import PasswordsTab from '@/components/PasswordsTab';
import ServiceSecretsTab from '@/components/ServiceSecretsTab';
import styles from './TreasureTab.module.css';

function TreasureTab() {
  const [dbGrants, setDbGrants] = useState<DatabaseGrant[]>([]);
  const [serverGrants, setServerGrants] = useState<ServerGrant[]>([]);
  const [dbLoading, setDbLoading] = useState(true);
  const [serverLoading, setServerLoading] = useState(true);
  const [dbSending, setDbSending] = useState(false);
  const [serverSending, setServerSending] = useState(false);
  const [expandedDbId, setExpandedDbId] = useState<string | null>(null);

  useEffect(() => {
    treasureGetDatabase()
      .then(setDbGrants)
      .catch(() => setDbGrants([]))
      .finally(() => setDbLoading(false));
    treasureGetServer()
      .then(setServerGrants)
      .catch(() => setServerGrants([]))
      .finally(() => setServerLoading(false));
  }, []);

  const handleSend = (type: 'database' | 'server') => {
    const isDb = type === 'database';
    Modal.confirm({
      title: isDb ? '发送数据库凭据' : '发送服务器凭据',
      content: isDb
        ? '系统将向您的注册邮箱发送数据库连接信息（含密码）。确认发送？'
        : '系统将向您的注册邮箱发送服务器 SSH 信息（含密码）。确认发送？',
      okText: '确认发送',
      cancelText: '取消',
      onOk: async () => {
        isDb ? setDbSending(true) : setServerSending(true);
        try {
          const r = isDb
            ? await treasureSendDatabaseCredentials()
            : await treasureSendServerCredentials();
          if (r.sent) message.success(`凭据已发送至 ${r.email}`);
          else message.warning(`发送失败：${r.error || '平台未配置邮件服务'}`);
        } catch {
          message.error('发送失败，请稍后再试');
        } finally {
          isDb ? setDbSending(false) : setServerSending(false);
        }
      },
    });
  };

  const dbContent = (
    <div>
      {dbLoading ? (
        <div className={styles.loadingArea}><Spin /></div>
      ) : dbGrants.length === 0 ? (
        <div className={styles.emptyArea}>暂无数据库权限</div>
      ) : (
        dbGrants.map((g) => {
          const isExpanded = expandedDbId === g.grant_id;
          return (
            <div key={g.grant_id} className={styles.grantCard}>
              <div
                className={g.tables.length > 0 ? styles.grantCardHeader : undefined}
                onClick={g.tables.length > 0 ? () => setExpandedDbId(isExpanded ? null : g.grant_id) : undefined}
              >
                <div className={styles.grantHost}>
                  <DatabaseOutlined style={{ marginRight: 6 }} />
                  {g.database.name || `${g.database.host}:${g.database.port}${g.database.db_name ? '/' + g.database.db_name : ''}`}
                </div>
                {g.tables.length > 0 && (
                  <div className={styles.grantCardRight}>
                    {!isExpanded && (
                      <span className={styles.tableCount}>{g.tables.length} 张表</span>
                    )}
                    {isExpanded
                      ? <DownOutlined className={styles.expandIcon} />
                      : <RightOutlined className={styles.expandIcon} />
                    }
                  </div>
                )}
              </div>
              <div className={styles.grantRow}>
                <span className={styles.grantLabel}>连接地址</span>
                <code className={styles.grantValue}>{g.database.host}:{g.database.port}{g.database.db_name ? '/' + g.database.db_name : ''}</code>
              </div>
              <div className={styles.grantRow}>
                <span className={styles.grantLabel}>用户名</span>
                <code className={styles.grantValue}>{g.database.username}</code>
              </div>
              {isExpanded && g.tables.length > 0 && (
                <div className={styles.grantRow}>
                  <span className={styles.grantLabel}>授权表</span>
                  <div className={styles.tagList}>
                    {g.tables.map((t) => (
                      <Tag
                        key={t.table_name}
                        color="rgba(0,255,255,0.08)"
                        style={{ color: 'rgba(0,255,255,0.7)', border: 'none', fontSize: 11 }}
                      >
                        {t.table_name}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
      <div className={styles.sendArea}>
        <Button
          icon={<MailOutlined />}
          loading={dbSending}
          className={styles.sendBtn}
          onClick={() => handleSend('database')}
          block
        >
          发送数据库凭据到邮箱
        </Button>
      </div>
    </div>
  );

  const serverContent = (
    <div>
      {serverLoading ? (
        <div className={styles.loadingArea}><Spin /></div>
      ) : serverGrants.length === 0 ? (
        <div className={styles.emptyArea}>暂无服务器权限</div>
      ) : (
        serverGrants.map((g) => (
          <div key={g.grant_id} className={styles.grantCard}>
            <div className={styles.grantHost}>
              <CloudServerOutlined style={{ marginRight: 6 }} />
              {g.server.name || `${g.server.host}:${g.server.port}`}
            </div>
            <div className={styles.grantRow}>
              <span className={styles.grantLabel}>连接地址</span>
              <code className={styles.grantValue}>{g.server.host}:{g.server.port}</code>
            </div>
            <div className={styles.grantRow}>
              <span className={styles.grantLabel}>SSH 用户名</span>
              <code className={styles.grantValue}>{g.server.ssh_username}</code>
            </div>
          </div>
        ))
      )}
      <div className={styles.sendArea}>
        <Button
          icon={<MailOutlined />}
          loading={serverSending}
          className={styles.sendBtn}
          onClick={() => handleSend('server')}
          block
        >
          发送服务器凭据到邮箱
        </Button>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>我的宝箱</div>
        <div className={styles.headerSub}>DATA · ACCESS · VAULT</div>
      </div>
      <div className={styles.body}>
        <Tabs
          items={[
            {
              key: 'database',
              label: <span><DatabaseOutlined /> 数据库</span>,
              children: dbContent,
            },
            {
              key: 'server',
              label: <span><CloudServerOutlined /> 服务器</span>,
              children: serverContent,
            },
            {
              key: 'passwords',
              label: <span><LockOutlined /> 私人密码</span>,
              children: (
                <div style={{ padding: '0 0 16px' }}>
                  <PasswordsTab mobile />
                </div>
              ),
            },
            {
              key: 'service-secrets',
              label: <span><KeyOutlined /> 服务密钥</span>,
              children: (
                <div style={{ padding: '0 16px 16px' }}>
                  <ServiceSecretsTab mobile />
                </div>
              ),
            },
          ]}
          style={{ padding: '0 4px' }}
        />
      </div>
    </div>
  );
}

export default TreasureTab;
